import { FC, useRef, useEffect, useState } from "react";
import { useDataTableStore } from "../../stores/DataTableStore";
import { useFormik } from "formik";
import * as Yup from 'yup';
import Swal from "sweetalert2";
import clsx from "clsx";
import useAccessToken from "../../hooks/AccessToken";
import { useIntl } from "react-intl";
import { KTSVG } from "../../_metronic/helpers";
import useUser from "../../hooks/User";
import useHandleCloseModal from "../../hooks/HandleCloseModal";
import { createEmail } from "../../api/MasterEmailCRUD";
import { insertAttachment } from "../../api/ResourceAPI";
import { handleCaps } from "../../functions/general";
import axios from "axios";
import useSWR, { mutate } from "swr";
import Select from "react-select";
import { SelectOption } from "../../interfaces/SelectOption";
import { UserCompany } from "../../interfaces/UserCompany";
import { generatePassword } from "../../functions/email";
import VirtualizedSelect from "../Select/SelectVirtualized";
import { nanoid } from "nanoid";
import { useEmailStore } from "../../stores/EmailStore";

const API_URL = process.env.REACT_APP_API_URL

const AddEmail = () => {
    const user = useUser();
    const intl = useIntl();
    const token = useAccessToken();
    const { tablesData, setTablesData } = useDataTableStore();
    const modalRef = useRef<HTMLDivElement | null>(null);
    const closeModalRef = useRef<HTMLButtonElement>(null);
    const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
    const [selectedUser, setSelectedUser] = useState<SelectOption[]>([]);
    const avatarInputRef = useRef<HTMLInputElement>(null);
    const { setDataLimitMasterEmail, dataLimitMasterEmail } = useEmailStore();


    const fetcher = (url: string) => axios.get(url, {
        headers: {
            'X-Auth-token': token
        }
    }).then(res => res.data.data)

    const { data: usersCompany = [], isLoading: userLoadingCompany } = useSWR(`${API_URL}user/company`, fetcher)

    useEffect(() => {
        if (!userLoadingCompany && usersCompany) {
            const options = usersCompany.map((user: UserCompany) => ({
                value: user.id,
                label: user.name
            }))
            setSelectedUser(options)
        }
    }, [usersCompany])

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            userId: '',
            firstName: '',
            lastName: '',
            username: '',
            avatar: null as File | null,
            photoUrl: '',
        },
        validationSchema: Yup.object().shape({
            userId: Yup.string().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: 'User' })),
            firstName: Yup.string().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: 'First Name' })),
            lastName: Yup.string().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: 'Last Name' })),
            username: Yup.string().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: 'Username' })),
            avatar: Yup.mixed().nullable(),
            photoUrl: Yup.string(),
        }),
        onSubmit: async (values, { setSubmitting }) => {
            setSubmitting(true);

            try {
                let photoUrl = values.photoUrl;

                if (values.avatar) {
                    const attachmentResponse = await insertAttachment(values.avatar, 'PROFILE PICTURE', token, 'Wooblazz MAIL');
                    photoUrl = attachmentResponse.data.data[0]; // Assuming the API returns the URL in this format
                }

                const createEmailData = {
                    email: `${values.username}@${user.data.domainName}`,
                    password: generatePassword(),
                    firstName: values.firstName,
                    lastName: values.lastName,
                    userId: values.userId,
                    companyId: user.data.company_id,
                    photoUrl: photoUrl,
                };

                const createEmailFunction = createEmail(token, createEmailData);
                const response = await createEmailFunction(`${API_URL}email/create-email`);
                if (response) {
                    Swal.fire({
                        icon: 'success',
                        heightAuto: false,
                        title: `${intl.formatMessage({ id: "FORM.VALIDATION.ADD_EMAIL_SUCCESS" })}`,
                        timer: 1500,
                        timerProgressBar: true
                    });
                    closeModalRef.current?.click();
                    mutate(`${API_URL}email/master-emails?companyId=${user.data.company_id}`)
                    setDataLimitMasterEmail({
                        totalEmails: dataLimitMasterEmail.totalEmails + 1,
                        limitEmails: dataLimitMasterEmail.limitEmails,
                    })
                    // setTablesData('emails', ([response.data, ...tablesData.emails]));
                }
            } catch (error: any) {
                Swal.fire({
                    icon: 'error',
                    title: error.response?.data?.message || 'An error occurred',
                    confirmButtonText: 'Ok',
                    heightAuto: false,
                });
            } finally {
                setSubmitting(false);
                if (avatarInputRef.current) {
                    avatarInputRef.current.value = '';
                }
            }
        }
    });


    useEffect(() => {
        const modalElement = modalRef.current;

        const handleModalHidden = () => {
            formik.resetForm();
            setAvatarPreview(null);
        };

        modalElement?.addEventListener('hidden.bs.modal', handleModalHidden);

        return () => {
            modalElement?.removeEventListener('hidden.bs.modal', handleModalHidden);
        };
    }, [formik]);

    const { handleCloseModal } = useHandleCloseModal(modalRef, closeModalRef, formik);

    const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.currentTarget.files?.[0];
        if (file) {
            formik.setFieldValue('avatar', file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setAvatarPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        } else {
            formik.setFieldValue('avatar', null);
            setAvatarPreview(null);
        }
    };

    return (
        <div>
            <button type="button" className="btn btn-primary mt-3" data-bs-toggle="modal" data-bs-target="#add-email-modal">
                <i className="fas fa-plus"></i> {intl.formatMessage({ id: 'FORM.ACTION.ADD' })} Email
            </button>
            <div className="modal fade" tabIndex={-1} id="add-email-modal" ref={modalRef}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">{intl.formatMessage({ id: 'FORM.ACTION.ADD' })} Email</h5>
                            <div
                                className="btn btn-icon btn-sm btn-active-light-primary ms-2"
                                onClick={handleCloseModal}
                            >
                                <KTSVG
                                    path="/media/icons/duotune/arrows/arr061.svg"
                                    className="svg-icon svg-icon-2x"
                                />
                            </div>
                        </div>
                        <form onSubmit={formik.handleSubmit} noValidate>
                            <div className="modal-body">
                                {formik.status && (
                                    <div className='mb-lg-15 alert alert-danger'>
                                        <div className='alert-text font-weight-bold'>{formik.status}</div>
                                    </div>
                                )}

                                <label htmlFor="users" className="form-label fs-6 fw-bolder text-dark required">{intl.formatMessage({ id: 'FORM.LABEL.USER' })}</label>
                                <div className="input-group mb-3">
                                    <VirtualizedSelect
                                        id="users"
                                        className={clsx("w-100")}
                                        placeholder={`${intl.formatMessage({ id: 'FORM.ACTION.CHOOSE' })} ${intl.formatMessage({ id: 'FORM.LABEL.USER' })}`}
                                        value={selectedUser.find(option => option.value === formik.values.userId) || null}
                                        options={selectedUser}
                                        onChange={(option) => formik.handleChange({ target: { name: "userId", value: option?.value || "" } })}
                                    />
                                </div>
                                {formik.touched.userId && formik.errors.userId && (
                                    <div className='fv-plugins-message-container text-danger'>
                                        <span role='alert' className="text-danger">{formik.errors.userId}</span>
                                    </div>
                                )}

                                {/* First Name */}
                                <label className='form-label fs-6 fw-bolder text-dark required'>{intl.formatMessage({ id: 'FORM.LABEL.FIRST_NAME' })}</label>
                                <div className="input-group mb-3">
                                    <input
                                        placeholder='First Name'
                                        {...formik.getFieldProps('firstName')}
                                        className={clsx(
                                            'form-control form-control-lg',
                                            { 'is-invalid': formik.touched.firstName && formik.errors.firstName },
                                        )}
                                        type='text'
                                        name='firstName'
                                        autoComplete='off'
                                        onInput={(e) => handleCaps(e)}
                                    />
                                </div>
                                {formik.touched.firstName && formik.errors.firstName && (
                                    <div className='fv-plugins-message-container text-danger'>
                                        <span role='alert' className="text-danger">{formik.errors.firstName}</span>
                                    </div>
                                )}

                                {/* Last Name */}
                                <label className='form-label fs-6 fw-bolder text-dark required'>{intl.formatMessage({ id: 'FORM.LABEL.LAST_NAME' })}</label>
                                <div className="input-group mb-3">
                                    <input
                                        placeholder='Last Name'
                                        {...formik.getFieldProps('lastName')}
                                        className={clsx(
                                            'form-control form-control-lg',
                                            { 'is-invalid': formik.touched.lastName && formik.errors.lastName },
                                        )}
                                        type='text'
                                        name='lastName'
                                        autoComplete='off'
                                        onInput={(e) => handleCaps(e)}

                                    />
                                </div>
                                {formik.touched.lastName && formik.errors.lastName && (
                                    <div className='fv-plugins-message-container text-danger'>
                                        <span role='alert' className="text-danger">{formik.errors.lastName}</span>
                                    </div>
                                )}

                                {/* Email */}
                                <label className='form-label fs-6 fw-bolder text-dark required'>Email</label>
                                <div className="input-group mb-3">
                                    <input
                                        placeholder='Username'
                                        {...formik.getFieldProps('username')}
                                        className={clsx(
                                            'form-control form-control-lg txt-email',
                                            { 'is-invalid': formik.touched.username && formik.errors.username },
                                        )}
                                        type='text'
                                        name='username'
                                        onKeyDown={(e) => { if (e.key === ' ') { e.preventDefault() } }}
                                        autoComplete='off'
                                    />
                                    <input
                                        type="text"
                                        className="form-control form-control-lg txt-email"
                                        value={"@" + user.data.domainName}
                                        disabled
                                    />
                                </div>
                                {formik.touched.username && formik.errors.username && (
                                    <div className='fv-plugins-message-container text-danger'>
                                        <span role='alert' className="text-danger">{formik.errors.username}</span>
                                    </div>
                                )}
                                {/* Avatar */}
                                <label className='form-label fs-6 fw-bolder text-dark'>Avatar ({intl.formatMessage({ id: 'FORM.LABEL.OPTIONAL' })})</label>
                                <div className="mb-5 position-relative">
                                    <img
                                        className="mb-3"
                                        style={{ width: '100px', height: '100px', borderRadius: '50%' }}
                                        src={avatarPreview || '/media/icons/duotune/communication/com013.svg'}
                                        alt='user-icon'
                                    />
                                    {formik.values.avatar && (
                                        <button
                                            type="button"
                                            className="btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow"
                                            style={{
                                                position: 'absolute',
                                                top: '1px',
                                                left: '-7px',
                                            }}
                                            onClick={() => {
                                                formik.setFieldValue('avatar', null);
                                                setAvatarPreview(null);
                                                if (avatarInputRef.current) {
                                                    avatarInputRef.current.value = '';
                                                }
                                            }}
                                        >
                                            <i className="bi bi-x fs-2"></i>
                                        </button>
                                    )}
                                    <input
                                        ref={avatarInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={handleAvatarChange}
                                        className="form-control form-control-lg"
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-light d-none"
                                    data-bs-dismiss="modal"
                                    ref={closeModalRef}
                                >
                                    {intl.formatMessage({ id: "FORM.ACTION.CANCEL" })}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-light"
                                    onClick={handleCloseModal}
                                >
                                    {intl.formatMessage({ id: "FORM.ACTION.CANCEL" })}
                                </button>
                                <button type="submit" className={`btn btn-primary me-10 ${formik.isSubmitting ? 'disabled' : ''}`} data-kt-indicator={formik.isSubmitting ? 'on' : 'off'}>
                                    <span className="indicator-label">
                                        {intl.formatMessage({ id: 'FORM.ACTION.ADD' })}
                                    </span>
                                    <span className="indicator-progress">
                                        Loading... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                    </span>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    )
}

export { AddEmail }